import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '€0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '€0.00';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function getStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
    case 'discontinued':
    case 'eol':
    case 'archived':
      return 'bg-gray-100 text-gray-800';
    case 'prototype':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getStockStatusColor(qty: number | string | null | undefined): string {
  // Handle string status values
  if (typeof qty === 'string') {
    switch (qty.toLowerCase()) {
      case 'out_of_stock':
      case 'out':
        return 'bg-red-100 text-red-800';
      case 'low_stock':
      case 'low':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_stock':
      case 'ok':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
  
  // Handle numeric values
  if (qty === null || qty === undefined) return 'bg-gray-100 text-gray-800';
  if (qty <= 0) return 'bg-red-100 text-red-800';
  if (qty <= 5) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function truncate(str: string, length: number): string {
  if (!str) return '';
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function generateOrderNumber(prefix: string, sequence: number): string {
  return `${prefix}-${sequence.toString().padStart(5, '0')}`;
}

/**
 * Business-hours model used throughout the app.
 *
 *  - Mon–Fri only
 *  - Window: 08:00–17:00 (9 wall-clock hours)
 *  - A 1-hour break is taken somewhere inside the 12:00–14:00 window.
 *    We don't know exactly when each tech took their break, so we model
 *    it as a "break zone": if a work span overlaps the 12:00–14:00
 *    window, up to 1 hour of that overlap is deducted (capped at the
 *    break length).
 *  - Effective working day = 8 hours.
 *  - If `activeDays` is provided, only days present in that set count —
 *    this is how unmanaged technicians are restricted to days they were
 *    signed in (see getTechActiveDays).
 */
export const BUSINESS_HOURS = {
  OPEN: 8,
  CLOSE: 17,
  /** Earliest the break can start. */
  BREAK_WINDOW_START: 12,
  /** Latest the break can end. */
  BREAK_WINDOW_END: 14,
  /** Actual break length (1 hour) — deducted when a span overlaps the window. */
  BREAK_LENGTH_MS: 60 * 60 * 1000,
  /** Working milliseconds per full business day: 8h. */
  DAY_MS: 8 * 3_600_000,
} as const;

/**
 * Calculate milliseconds of business time between two dates. Honours the
 * 08:00–17:00 window, the 12:00–14:00 break-zone deduction, and an
 * optional `activeDays` filter for unmanaged techs.
 */
export function businessMs(start: Date, end: Date, activeDays?: Set<string>, skipLunch = false): number {
  if (end <= start) return 0;
  const { OPEN, CLOSE, BREAK_WINDOW_START, BREAK_WINDOW_END, BREAK_LENGTH_MS } = BUSINESS_HOURS;
  let total = 0;
  const cur = new Date(start);

  while (cur < end) {
    const dow = cur.getDay();
    if (dow >= 1 && dow <= 5) { // Mon–Fri
      // If activeDays provided, skip days the tech wasn't signed in
      if (activeDays) {
        const dayKey = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        if (!activeDays.has(dayKey)) {
          cur.setDate(cur.getDate() + 1);
          cur.setHours(0, 0, 0, 0);
          continue;
        }
      }
      const dayStart = new Date(cur); dayStart.setHours(OPEN, 0, 0, 0);
      const dayEnd   = new Date(cur); dayEnd.setHours(CLOSE, 0, 0, 0);
      const overlapStart = cur > dayStart ? cur : dayStart;
      const overlapEnd   = end < dayEnd ? end : dayEnd;
      if (overlapEnd > overlapStart) {
        let dayOverlap = overlapEnd.getTime() - overlapStart.getTime();
        // Deduct break time: how much of the overlap falls inside the
        // 12:00–14:00 break window, capped at BREAK_LENGTH_MS.
        // Skipped when skipLunch=true (used to compute wall-clock stage
        // durations before lunch reattribution in step 6 of buildStageTimeline).
        if (!skipLunch) {
          const breakWindowStart = new Date(cur); breakWindowStart.setHours(BREAK_WINDOW_START, 0, 0, 0);
          const breakWindowEnd   = new Date(cur); breakWindowEnd.setHours(BREAK_WINDOW_END, 0, 0, 0);
          const breakOverlapStart = overlapStart > breakWindowStart ? overlapStart : breakWindowStart;
          const breakOverlapEnd   = overlapEnd   < breakWindowEnd   ? overlapEnd   : breakWindowEnd;
          if (breakOverlapEnd > breakOverlapStart) {
            const breakOverlapMs = breakOverlapEnd.getTime() - breakOverlapStart.getTime();
            dayOverlap -= Math.min(breakOverlapMs, BREAK_LENGTH_MS);
          }
        }
        total += dayOverlap;
      }
    }
    // Move to next day 00:00
    cur.setDate(cur.getDate() + 1);
    cur.setHours(0, 0, 0, 0);
  }
  return Math.min(total, (end.getTime() - start.getTime())); // never exceed wall-clock
}

/**
 * Advance a Date by N milliseconds of *business* time and return the
 * resulting wall-clock Date.
 *
 * Matches the model in businessMs(): Mon–Fri, 08:00–17:00 window, with a
 * 1-hour break deducted from the 12:00–14:00 break zone. The first hour
 * of any walk that intersects the break zone is treated as "break time"
 * (not counted as business time), so the cursor advances through the
 * break without accumulating business ms — and later the remaining 1h of
 * the zone counts as work.
 *
 * If `start` is itself outside business hours, it's first snapped forward
 * to the next valid business moment.
 */
export function addBusinessMs(start: Date, ms: number, activeDays?: Set<string>): Date {
  const { OPEN, CLOSE, BREAK_WINDOW_START, BREAK_LENGTH_MS } = BUSINESS_HOURS;
  const cur = new Date(start);

  const dayKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  /** Move `cur` forward to the next valid in-hours business moment. */
  const snapForward = () => {
    // Cap iterations as a defensive guard against pathological inputs.
    for (let guard = 0; guard < 366 * 5; guard++) {
      const dow = cur.getDay();
      const isWeekend = dow === 0 || dow === 6;
      if (isWeekend) {
        cur.setDate(cur.getDate() + 1);
        cur.setHours(OPEN, 0, 0, 0);
        continue;
      }
      if (activeDays && !activeDays.has(dayKey(cur))) {
        cur.setDate(cur.getDate() + 1);
        cur.setHours(OPEN, 0, 0, 0);
        continue;
      }
      const hour = cur.getHours();
      if (hour < OPEN) {
        cur.setHours(OPEN, 0, 0, 0);
        continue;
      }
      if (hour >= CLOSE) {
        cur.setDate(cur.getDate() + 1);
        cur.setHours(OPEN, 0, 0, 0);
        continue;
      }
      return;
    }
  };

  snapForward();
  let remaining = Math.max(0, ms);

  for (let guard = 0; remaining > 0 && guard < 366 * 5; guard++) {
    // Skip break time (first 1h inside 12:00–14:00) by jumping the cursor
    // forward when it's inside the break zone and we still owe break time
    // on this day. Modelled as: the FIRST 1h of the break window is
    // "break", the remainder is work.
    const breakStart = new Date(cur); breakStart.setHours(BREAK_WINDOW_START, 0, 0, 0);
    const breakSkipEnd = new Date(cur);
    breakSkipEnd.setHours(BREAK_WINDOW_START, 0, 0, 0);
    breakSkipEnd.setTime(breakSkipEnd.getTime() + BREAK_LENGTH_MS);
    if (cur >= breakStart && cur < breakSkipEnd) {
      cur.setTime(breakSkipEnd.getTime());
      continue; // re-check snap / break on next loop iteration
    }

    const dayEnd = new Date(cur);
    dayEnd.setHours(CLOSE, 0, 0, 0);
    const availableToday = dayEnd.getTime() - cur.getTime();

    // If the cursor is still before the break zone, stop at breakStart
    // first so the next iteration can skip the break.
    let segmentEnd = availableToday;
    if (cur < breakStart) {
      segmentEnd = Math.min(segmentEnd, breakStart.getTime() - cur.getTime());
    }

    if (remaining <= segmentEnd) {
      cur.setTime(cur.getTime() + remaining);
      remaining = 0;
    } else if (segmentEnd < availableToday) {
      // Walked up to the break boundary; next iteration will skip it.
      cur.setTime(cur.getTime() + segmentEnd);
      remaining -= segmentEnd;
    } else {
      // Walked the rest of the day; advance to the next working day.
      remaining -= availableToday;
      cur.setDate(cur.getDate() + 1);
      cur.setHours(OPEN, 0, 0, 0);
      snapForward();
    }
  }

  return cur;
}

/** Format business-hours duration as "Xh Ym". Pass skipLunch=true for wall-clock business hours. */
export function formatBusinessDuration(startDate: string | Date, endDate: string | Date, activeDays?: Set<string>, skipLunch = false): string {
  const s = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const e = typeof endDate   === 'string' ? new Date(endDate)   : endDate;
  const ms = businessMs(s, e, activeDays, skipLunch);
  const hours = Math.floor(ms / 3_600_000);
  const mins  = Math.round((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * Returns the total lunch break time (ms) included in the wall-clock
 * business span. Equals the difference between skipLunch=true and
 * skipLunch=false, which is always a multiple of BREAK_LENGTH_MS (1h).
 */
export function calcLunchBreaksMs(start: Date, end: Date, activeDays?: Set<string>): number {
  return Math.max(0, businessMs(start, end, activeDays, true) - businessMs(start, end, activeDays, false));
}

/** Calculate business days between two dates (8h effective day). */
export function businessDays(start: Date, end: Date, activeDays?: Set<string>): number {
  const ms = businessMs(start, end, activeDays);
  return Math.max(1, Math.ceil(ms / BUSINESS_HOURS.DAY_MS));
}

// getTechActiveDays, getTechManagedStatus and getActiveDaysIfUnmanaged moved to
// lib/api/techActivity.ts. They were the only database access in this module,
// and their supabase import made every pure helper here depend on a populated
// .env at module load.

/**
 * Auto-format a Build SKU as the user types.
 * Standard: AOI-<FamilyPlatform>-<GenNum>[-<RevNum>][-NL]
 * e.g. "aoi1ag4r1" → "AOI-1A-G4-R1", "aoimag3nl" → "AOI-MA-G3-NL"
 * Custom: AOI-CA-... or AOI-CI-... — uppercase only, user controls hyphens.
 */
export function formatBuildSku(raw: string): string {
  // Collapse double hyphens and trailing spaces, uppercase everything
  let val = raw.toUpperCase().replace(/-{2,}/g, '-').replace(/\s+/g, '');
  if (!val) return '';

  // If user typed hyphens, respect their structure — just clean up
  if (raw.includes('-')) {
    // Don't allow hyphen as first char
    if (val.startsWith('-')) val = val.slice(1);
    return val;
  }

  // No hyphens typed — auto-format
  const clean = val;
  if (!clean.startsWith('AOI') && !'AOI'.startsWith(clean)) return clean;
  if (clean.length <= 3) return clean;

  const rest = clean.slice(3);

  // Custom builds (CA, CI) — uppercase only, single hyphen after AOI
  if (rest.startsWith('CA') || rest.startsWith('CI')) {
    return 'AOI-' + rest;
  }

  // Standard builds — auto-hyphenate segments
  const match = rest.match(/^([A-Z0-9]{2})(G\d+)?(R\d+)?(NL)?(.*)$/i);
  if (!match) return 'AOI-' + rest;

  const parts = ['AOI', match[1]];
  if (match[2]) parts.push(match[2]);
  if (match[3]) parts.push(match[3]);
  if (match[4]) parts.push(match[4]);
  if (match[5]) parts[parts.length - 1] += match[5];
  return parts.join('-');
}
